import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resumeAPI, aiAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  Save, 
  Sparkles, 
  Plus, 
  Trash2, 
  Loader2,
  User,
  Briefcase,
  GraduationCap,
  Wrench
} from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';

const ResumeEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = id && id !== 'new';

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  const [resume, setResume] = useState({
    title: 'My Resume',
    personal_info: {
      name: '',
      email: '',
      phone: '',
      location: '',
      linkedin: '',
      website: '',
    },
    summary: '',
    experience: [],
    education: [],
    skills: [],
  });

  const [newSkill, setNewSkill] = useState('');

  useEffect(() => {
    if (isEdit) {
      const fetchResume = async () => {
        try {
          const response = await resumeAPI.getOne(id);
          setResume(response.data);
        } catch (error) {
          toast.error('Failed to load resume');
          navigate('/resumes');
        } finally {
          setLoading(false);
        }
      };
      fetchResume();
    }
  }, [id, isEdit, navigate]);

  const handleSave = async () => {
    if (!resume.title) {
      toast.error('Please enter a resume title');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await resumeAPI.update(id, resume);
        toast.success('Resume updated!');
      } else {
        const response = await resumeAPI.create(resume);
        toast.success('Resume created!');
        navigate(`/resumes/${response.data.id}/edit`);
      }
    } catch (error) {
      toast.error('Failed to save resume');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSummary = async () => {
    const { name, email } = resume.personal_info;
    const expSummary = resume.experience.map(e => `${e.title} at ${e.company}`).join(', ');
    const skillsSummary = resume.skills.join(', ');

    if (!name && !expSummary && !skillsSummary) {
      toast.error('Please add some information first');
      return;
    }

    setGenerating(true);
    try {
      const prompt = `Name: ${name || 'Professional'}. Experience: ${expSummary || 'Various roles'}. Skills: ${skillsSummary || 'Multiple skills'}`;
      const response = await aiAPI.generateSummary(prompt);
      setResume({ ...resume, summary: response.data.content });
      toast.success('Summary generated!');
    } catch (error) {
      toast.error('Failed to generate summary');
    } finally {
      setGenerating(false);
    }
  };

  const updatePersonalInfo = (field, value) => {
    setResume({
      ...resume,
      personal_info: { ...resume.personal_info, [field]: value },
    });
  };

  const addExperience = () => {
    setResume({
      ...resume,
      experience: [
        ...resume.experience,
        { company: '', title: '', startDate: '', endDate: '', description: '' },
      ],
    });
  };

  const updateExperience = (index, field, value) => {
    const newExp = [...resume.experience];
    newExp[index] = { ...newExp[index], [field]: value };
    setResume({ ...resume, experience: newExp });
  };

  const removeExperience = (index) => {
    setResume({
      ...resume,
      experience: resume.experience.filter((_, i) => i !== index),
    });
  };

  const addEducation = () => {
    setResume({
      ...resume,
      education: [
        ...resume.education,
        { school: '', degree: '', field: '', startDate: '', endDate: '' },
      ],
    });
  };

  const updateEducation = (index, field, value) => {
    const newEdu = [...resume.education];
    newEdu[index] = { ...newEdu[index], [field]: value };
    setResume({ ...resume, education: newEdu });
  };

  const removeEducation = (index) => {
    setResume({
      ...resume,
      education: resume.education.filter((_, i) => i !== index),
    });
  };

  const addSkill = () => {
    if (newSkill.trim() && !resume.skills.includes(newSkill.trim())) {
      setResume({ ...resume, skills: [...resume.skills, newSkill.trim()] });
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setResume({ ...resume, skills: resume.skills.filter((s) => s !== skill) });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="resume-editor-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/resumes')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <Input
                value={resume.title}
                onChange={(e) => setResume({ ...resume, title: e.target.value })}
                className="text-2xl font-bold border-0 bg-transparent p-0 h-auto focus-visible:ring-0"
                placeholder="Resume Title"
                data-testid="resume-title-input"
              />
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-resume-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Resume
          </Button>
        </div>

        {/* Editor Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Form Section */}
          <div className="space-y-6">
            <Tabs defaultValue="personal" className="w-full">
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="personal" data-testid="tab-personal">
                  <User className="w-4 h-4 mr-2 hidden sm:block" />
                  Personal
                </TabsTrigger>
                <TabsTrigger value="experience" data-testid="tab-experience">
                  <Briefcase className="w-4 h-4 mr-2 hidden sm:block" />
                  Experience
                </TabsTrigger>
                <TabsTrigger value="education" data-testid="tab-education">
                  <GraduationCap className="w-4 h-4 mr-2 hidden sm:block" />
                  Education
                </TabsTrigger>
                <TabsTrigger value="skills" data-testid="tab-skills">
                  <Wrench className="w-4 h-4 mr-2 hidden sm:block" />
                  Skills
                </TabsTrigger>
              </TabsList>

              <TabsContent value="personal" className="space-y-4 mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Personal Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Full Name</Label>
                        <Input
                          value={resume.personal_info.name}
                          onChange={(e) => updatePersonalInfo('name', e.target.value)}
                          placeholder="John Doe"
                          data-testid="input-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={resume.personal_info.email}
                          onChange={(e) => updatePersonalInfo('email', e.target.value)}
                          placeholder="john@example.com"
                          data-testid="input-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          value={resume.personal_info.phone}
                          onChange={(e) => updatePersonalInfo('phone', e.target.value)}
                          placeholder="+1 (555) 000-0000"
                          data-testid="input-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Location</Label>
                        <Input
                          value={resume.personal_info.location}
                          onChange={(e) => updatePersonalInfo('location', e.target.value)}
                          placeholder="New York, NY"
                          data-testid="input-location"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>LinkedIn</Label>
                        <Input
                          value={resume.personal_info.linkedin}
                          onChange={(e) => updatePersonalInfo('linkedin', e.target.value)}
                          placeholder="linkedin.com/in/johndoe"
                          data-testid="input-linkedin"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Website</Label>
                        <Input
                          value={resume.personal_info.website}
                          onChange={(e) => updatePersonalInfo('website', e.target.value)}
                          placeholder="johndoe.com"
                          data-testid="input-website"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Professional Summary</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateSummary}
                      disabled={generating}
                      data-testid="generate-summary-btn"
                    >
                      {generating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      Generate with AI
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={resume.summary}
                      onChange={(e) => setResume({ ...resume, summary: e.target.value })}
                      placeholder="A brief summary of your professional background..."
                      rows={5}
                      data-testid="input-summary"
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="experience" className="space-y-4 mt-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">Work Experience</h3>
                  <Button variant="outline" onClick={addExperience} data-testid="add-experience-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Experience
                  </Button>
                </div>
                {resume.experience.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-slate-500">
                      No experience added yet. Click "Add Experience" to get started.
                    </CardContent>
                  </Card>
                ) : (
                  resume.experience.map((exp, index) => (
                    <Card key={index} data-testid={`experience-card-${index}`}>
                      <CardContent className="pt-6 space-y-4">
                        <div className="flex items-start justify-between">
                          <h4 className="font-medium">Experience #{index + 1}</h4>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeExperience(index)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Company</Label>
                            <Input
                              value={exp.company}
                              onChange={(e) => updateExperience(index, 'company', e.target.value)}
                              placeholder="Company Name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Job Title</Label>
                            <Input
                              value={exp.title}
                              onChange={(e) => updateExperience(index, 'title', e.target.value)}
                              placeholder="Software Engineer"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Start Date</Label>
                            <Input
                              value={exp.startDate}
                              onChange={(e) => updateExperience(index, 'startDate', e.target.value)}
                              placeholder="Jan 2020"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>End Date</Label>
                            <Input
                              value={exp.endDate}
                              onChange={(e) => updateExperience(index, 'endDate', e.target.value)}
                              placeholder="Present"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Textarea
                            value={exp.description}
                            onChange={(e) => updateExperience(index, 'description', e.target.value)}
                            placeholder="Describe your responsibilities and achievements..."
                            rows={3}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="education" className="space-y-4 mt-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">Education</h3>
                  <Button variant="outline" onClick={addEducation} data-testid="add-education-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Education
                  </Button>
                </div>
                {resume.education.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-slate-500">
                      No education added yet. Click "Add Education" to get started.
                    </CardContent>
                  </Card>
                ) : (
                  resume.education.map((edu, index) => (
                    <Card key={index} data-testid={`education-card-${index}`}>
                      <CardContent className="pt-6 space-y-4">
                        <div className="flex items-start justify-between">
                          <h4 className="font-medium">Education #{index + 1}</h4>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeEducation(index)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>School</Label>
                            <Input
                              value={edu.school}
                              onChange={(e) => updateEducation(index, 'school', e.target.value)}
                              placeholder="University Name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Degree</Label>
                            <Input
                              value={edu.degree}
                              onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                              placeholder="Bachelor's"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Field of Study</Label>
                            <Input
                              value={edu.field}
                              onChange={(e) => updateEducation(index, 'field', e.target.value)}
                              placeholder="Computer Science"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Graduation Year</Label>
                            <Input
                              value={edu.endDate}
                              onChange={(e) => updateEducation(index, 'endDate', e.target.value)}
                              placeholder="2020"
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="skills" className="space-y-4 mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Skills</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                        placeholder="Add a skill..."
                        onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                        data-testid="input-skill"
                      />
                      <Button onClick={addSkill} variant="outline" data-testid="add-skill-btn">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {resume.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-sm"
                        >
                          {skill}
                          <button
                            onClick={() => removeSkill(skill)}
                            className="hover:text-indigo-900"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Preview Section */}
          <div className="preview-container hidden lg:flex">
            <div className="resume-preview" data-testid="resume-preview">
              {/* Header */}
              <div className="text-center mb-6 pb-4 border-b border-slate-200">
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {resume.personal_info.name || 'Your Name'}
                </h1>
                <div className="flex flex-wrap justify-center gap-3 mt-2 text-sm text-slate-600">
                  {resume.personal_info.email && <span>{resume.personal_info.email}</span>}
                  {resume.personal_info.phone && <span>• {resume.personal_info.phone}</span>}
                  {resume.personal_info.location && <span>• {resume.personal_info.location}</span>}
                </div>
                <div className="flex flex-wrap justify-center gap-3 mt-1 text-sm text-indigo-600">
                  {resume.personal_info.linkedin && <span>{resume.personal_info.linkedin}</span>}
                  {resume.personal_info.website && <span>• {resume.personal_info.website}</span>}
                </div>
              </div>

              {/* Summary */}
              {resume.summary && (
                <div className="mb-6">
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2">
                    Professional Summary
                  </h2>
                  <p className="text-sm text-slate-600 leading-relaxed">{resume.summary}</p>
                </div>
              )}

              {/* Experience */}
              {resume.experience.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
                    Experience
                  </h2>
                  {resume.experience.map((exp, index) => (
                    <div key={index} className="mb-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-slate-900">{exp.title || 'Job Title'}</p>
                          <p className="text-sm text-slate-600">{exp.company || 'Company'}</p>
                        </div>
                        <span className="text-xs text-slate-500">
                          {exp.startDate} - {exp.endDate || 'Present'}
                        </span>
                      </div>
                      {exp.description && (
                        <p className="text-sm text-slate-600 mt-1">{exp.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Education */}
              {resume.education.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
                    Education
                  </h2>
                  {resume.education.map((edu, index) => (
                    <div key={index} className="mb-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-slate-900">
                            {edu.degree} {edu.field && `in ${edu.field}`}
                          </p>
                          <p className="text-sm text-slate-600">{edu.school || 'School'}</p>
                        </div>
                        <span className="text-xs text-slate-500">{edu.endDate}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Skills */}
              {resume.skills.length > 0 && (
                <div>
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2">
                    Skills
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {resume.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-700"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ResumeEditor;
